import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { MarkdownVaultAnchor, vaultUrlTransform } from '@/lib/vaultLink'
import type { Turn } from '@/stores/useConciergeStore'
import { ConciergeThinkingBlock } from './ConciergeThinkingBlock'
import { ConciergeToolPill } from './ConciergeToolPill'
import { ConciergeOptionCard } from './ConciergeOptionCard'
import { ConciergeFormCard } from './ConciergeFormCard'
import { ConciergeMultiSelectCard } from './ConciergeMultiSelectCard'
import { ConciergeWorkflowCard } from './ConciergeWorkflowCard'

// Fence classes that should render as cards instead of code blocks.
// One place to add new workflow-trigger types in the future without
// having to retouch the <pre> override.
const CONCIERGE_FENCE_CLASSES = new Set([
  'language-concierge-options',
  'language-concierge-multiselect',
  'language-concierge-form',
  'language-concierge-wrapup',
  'language-concierge-prep',
  'language-concierge-review',
])

interface Props {
  turn: Turn
  /** Stage a card answer in the chat composer (single-question
   *  `concierge-options`). The user can edit before pressing Enter.
   *  `replacePrevious` lets a card update its own prior pick in place
   *  (find-and-replace within the composer) so re-picking inside one
   *  card doesn't wipe the other cards' staged contributions. */
  onStage?: (chosen: string, replacePrevious?: string) => void
  /** Send a card answer directly as the next user turn (multi-question
   *  `concierge-form`, `concierge-multiselect`). These cards already
   *  let the user review picks inside the card before they hit Submit,
   *  so the staging step is redundant — just send. */
  onSend?: (text: string) => void
  /** True only for the most-recent assistant turn while no stream is in
   *  flight. Drives whether card controls are interactive or frozen. */
  isLive?: boolean
  /** Content of the user turn immediately after this assistant turn,
   *  if any. Lets cards recover historical answers across page reloads
   *  (chosen pill highlights persist even though the component-local
   *  state was wiped). */
  nextUserContent?: string
}

/**
 * One conversation turn. Visual language matches lovellarmand.com — dark
 * palette, no rounded corners, hairline borders only when needed.
 *   user      → right-aligned, subtle background tint (no chunky bubble)
 *   assistant → left-aligned, no background; just markdown text +
 *               thinking + tool pills above it
 *
 * GFM enabled (`remark-gfm`) so tables / strikethrough / task lists
 * render — the agent emits markdown tables for schedule rundowns and
 * without GFM they showed as raw `| pipes |` text.
 */
export function ConciergeChatMessage({
  turn,
  onStage,
  onSend,
  isLive,
  nextUserContent,
}: Props) {
  const isUser = turn.role === 'user'

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%]',
          // User: subtle background (matches the homepage's sidebar-bg
          // value at rgb(18,18,18) on dark mode — a hair darker than the
          // page bg). No rounded corners. No chunky padding.
          isUser
            ? 'px-3 py-2 bg-[rgb(18,18,18)] text-[rgb(235,235,235)]'
            : 'py-2',
          // Assistant turns get the per-mode left-edge accent inside a
          // workflow room (concierge-mode-active wrapper supplies the
          // accent color). Default-mode chat: no-op, this class has no
          // styling outside the wrapper.
          !isUser && 'concierge-assistant-accent'
        )}
      >
        {/* Assistant-only meta blocks above the markdown body. Order is
            deliberate: tool pills (what was gathered) → thinking (the
            reasoning) → content. Matches the streaming render order in
            ConciergeChat and the cognitive flow the reader expects. */}
        {!isUser && turn.toolCalls && turn.toolCalls.length > 0 && (
          <div className="mb-3 space-y-1">
            {turn.toolCalls.map((c) => (
              <ConciergeToolPill key={c.id} call={c} />
            ))}
          </div>
        )}

        {!isUser && turn.thinking && (
          <ConciergeThinkingBlock thinking={turn.thinking} isStreaming={false} />
        )}

        {/* Markdown body — same prose treatment for both roles; GFM
            enabled. rehypeRaw lets the agent emit inline HTML when it
            renders embedded option/wrap-up cards in a later iteration. */}
        <div
          className={cn(
            'concierge-prose text-[15px] leading-relaxed',
            isUser && 'text-[rgb(235,235,235)]'
          )}
        >
          <ReactMarkdown
            urlTransform={vaultUrlTransform}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              // Vault links — `[label](<Domain/.../note.md>)`. Left to the
              // browser they'd navigate the SPA to a non-route → blank screen.
              // The shared anchor opens the target node in the center pane;
              // external links keep default new-tab behavior.
              a: MarkdownVaultAnchor,
              // Detect ```concierge-* fenced blocks and render the
              // matching card instead of a code box. Cards always render
              // (live or historical) — the gate on whether buttons are
              // clickable lives inside each card via `isLive`.
              code(props) {
                const { className, children } = props
                const body = String(children ?? '')
                if (className === 'language-concierge-options') {
                  return (
                    <ConciergeOptionCard
                      body={body}
                      onStage={onStage ?? (() => {})}
                      isLive={!!isLive}
                      nextUserContent={nextUserContent}
                    />
                  )
                }
                if (className === 'language-concierge-multiselect') {
                  return (
                    <ConciergeMultiSelectCard
                      body={body}
                      onSend={onSend ?? (() => {})}
                      isLive={!!isLive}
                      nextUserContent={nextUserContent}
                    />
                  )
                }
                if (className === 'language-concierge-form') {
                  return (
                    <ConciergeFormCard
                      body={body}
                      onSend={onSend ?? (() => {})}
                      isLive={!!isLive}
                      nextUserContent={nextUserContent}
                    />
                  )
                }
                if (className === 'language-concierge-wrapup') {
                  return <ConciergeWorkflowCard mode="wrapup" body={body} />
                }
                if (className === 'language-concierge-prep') {
                  return <ConciergeWorkflowCard mode="prep" body={body} />
                }
                if (className === 'language-concierge-review') {
                  return <ConciergeWorkflowCard mode="review" body={body} />
                }
                return <code className={className}>{children}</code>
              },
              // ReactMarkdown wraps `code` in `<pre>` for block fences.
              // When we replace the code with our card, the wrapping
              // <pre> would still apply its monospace + padding. Detect
              // any of the concierge-* fence classes and pass through
              // without the <pre> wrapper.
              pre(props) {
                const { children } = props as { children: React.ReactNode }
                const child = Array.isArray(children) ? children[0] : children
                const cls =
                  child &&
                  typeof child === 'object' &&
                  'props' in child
                    ? (child as { props: { className?: string } }).props.className
                    : undefined
                if (cls && CONCIERGE_FENCE_CLASSES.has(cls)) {
                  return <>{children}</>
                }
                return <pre>{children}</pre>
              },
            }}
          >
            {turn.content.trim()}
          </ReactMarkdown>
        </div>

        <div
          className={cn(
            'text-[10px] mt-2 font-light tracking-wide',
            isUser
              ? 'text-[rgb(235,235,235)]/40'
              : 'text-muted-foreground/60'
          )}
        >
          {new Date(turn.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  )
}
